/**
 * THE answer to "will my modal / sheet paint above the phone bottom nav, and
 * will its buttons clear it?" (2026-10-01, `feat/mobile-nav-overlays`).
 *
 * THE DEFECT. `Layout.js`'s bottom nav is `lg:hidden fixed bottom-0 … z-50`
 * (64px, `h-16`) and so was nearly every modal overlay. Equal z-index resolves
 * by DOM order, so on a 360x640 phone Dept Hires › Kanban › Off hired ›
 * "Set final price" put its Cancel/Confirm row UNDER the nav, and a tap there
 * landed on a nav link. Raising the overlay alone is not enough: a centred
 * panel taller than the space above the nav still sits behind it, so below
 * `md` the overlay also reserves the nav's height as bottom padding and the
 * panel is capped to what is left (its body scrolls, never the page).
 *
 * 🔑 THE TIERS BELOW `md` (extends the z-index contract in
 * `components/ui/CLAUDE.md`):
 *    z-50   the bottom nav (`Layout.js`) — unchanged
 *    z-[55] `Grading.js`'s fixed action bar — a PAGE layer, not a modal
 *    z-[58] MODAL_OVERLAY_Z (`max-md:` only) — every modal overlay. Above
 *           both page layers, and deliberately BELOW the escape tier:
 *    60     portalled panels (`ui/Dropdown` `portal`, the kebab) and the
 *           bottom SHEET (`SHEET_Z`) — so a dropdown opened from inside a
 *           dialog still paints over that dialog.
 *    z-[60] impersonation banner, z-[100] toasts / bell popup — unchanged.
 *
 * 📱 EVERYTHING HERE IS `max-md:` ONLY (owner ruling 2026-10-01: "at md+ the
 * dialog z-index and max-height must stay exactly as on main"). So an
 * overlay KEEPS its own `z-50` (or `z-40`) exactly where `main` had it and
 * these classes are APPENDED; from `md` up its class list resolves exactly
 * as before. ⚠️ The nav is `lg:hidden`, so it is still on screen at
 * 768–1023px, where a modal ties it at z-50 again. Declared, not fixed —
 * the ruling chose that.
 *
 * 🪤 Every class here must stay a COMPLETE LITERAL — Tailwind's scanner reads
 * source text, and a class built at runtime compiles to nothing.
 */

/** Below `md`: a panel's Save / Discard (or a form's Submit) PINNED to the
 *  viewport bottom, just above the 64px nav — `ui/PhoneActionBar`, the same
 *  z-[55] PAGE layer as `Grading.js`'s bar (under every modal). Owner 2026-10-05:
 *  the buttons are spaced EVENLY across the full-width bar (`max-md:justify-evenly`),
 *  not bunched in the middle. `max-md:gap-x-3` = the bar's own `px-3`, so the
 *  gap between buttons equals the gap at each edge whatever a caller's `gap-*`. */
/** From `md` up (owner 2026-10-03): an edit's Save / Discard float CENTRED at the
 *  bottom while there are unsaved changes — the in-flow row is gone. Owner
 *  2026-10-05: centred on the PAGE AREA beside the sidebar, not the window — from
 *  `lg` the sidebar (Layout.js sets `--sidebar-width`: 4rem collapsed, 16rem open)
 *  shifts the centre by half its width; below `lg` there is no sidebar. */
export const DESKTOP_ACTION_BAR_PINNED = 'md:fixed md:bottom-4 md:left-1/2 lg:left-[calc(50%+var(--sidebar-width,0px)/2)] md:-translate-x-1/2 md:z-40 md:m-0 md:px-4 md:py-2 md:bg-white md:dark:bg-gray-800 md:border md:border-gray-200 md:dark:border-gray-700 md:rounded-xl md:shadow-lg';

export const PHONE_ACTION_BAR_PINNED = 'max-md:fixed max-md:inset-x-0 max-md:justify-evenly max-md:gap-x-3 max-md:bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] max-md:z-[55] max-md:m-0 max-md:px-3 max-md:py-2 max-md:bg-white max-md:dark:bg-gray-800 max-md:border-t max-md:dark:border-gray-700 max-md:shadow-[0_-2px_6px_rgba(0,0,0,0.08)] max-md:[&_button]:min-h-11';

/** The modal overlay's z tier below `md`. APPENDED beside the overlay's own
 *  `z-50`, which still governs from `md` up. */
export const MODAL_OVERLAY_Z = 'max-md:z-[58]';

/** Below `md`: keep the overlay's content box above the 64px nav, plus a
 *  16px gap. Overrides the bottom half of a `p-4`/`p-0`. */
export const MODAL_OVERLAY_NAV_CLEARANCE = 'max-md:pb-[calc(5rem+env(safe-area-inset-bottom,0px))]';

/** Tier + clearance, both phone-only: what a modal overlay APPENDS. */
export const MODAL_OVERLAY = `${MODAL_OVERLAY_Z} ${MODAL_OVERLAY_NAV_CLEARANCE}`;

/** Below `md`: the panel never outgrows the cleared overlay; it scrolls
 *  inside itself. A panel with its own inner scroller (a flex-col frame)
 *  keeps a pinned footer — the outer `overflow-y-auto` then never engages. */
export const MODAL_PANEL_MOBILE_CAP = 'max-md:max-h-full max-md:overflow-y-auto';

/** `ui/Dropdown`'s bottom sheet: the escape tier, as an inline style. */
export const SHEET_Z = 60;

/** The sheet's bottom edge: 8px above the 64px nav, plus any safe-area inset. */
export const SHEET_BOTTOM = 'calc(4rem + 0.5rem + env(safe-area-inset-bottom, 0px))';

/** Below `md`: a BOTTOM toast's edge (`ToastContext`'s opt-in `phone.bottom`, owner 2026-10-29) —
 *  clear of the 64px nav AND of a pinned `PHONE_ACTION_BAR_PINNED` bar above it (py-2 + a 44px
 *  button = 60px), plus 8px: 64 + 60 + 8 = 132px, so it never covers the bar's buttons. */
export const TOAST_PHONE_BOTTOM = 'calc(8.25rem + env(safe-area-inset-bottom, 0px))';

/** 🍪 R-2 (measured 2026-10-29, 390×844, `e2e-local/tick-keeps-row-open.spec.js`): on a FIRST visit
 *  the cookie banner (`NAV_CLEAR_BOTTOM`, z-50) occupied y 631.5–772 and the bottom toast (z-[100])
 *  y 646–712 — painted over the banner's text. While it shows, the banner publishes its own top edge
 *  (+8px) as this custom property on `<html>`, and the bottom toast host takes the HIGHER of the two
 *  edges. Unset (no banner) → `TOAST_PHONE_BOTTOM` exactly as before. */
export const CONSENT_BANNER_CLEAR_VAR = '--consent-banner-clear';
export const TOAST_PHONE_BOTTOM_HOST = `max(${TOAST_PHONE_BOTTOM}, var(${CONSENT_BANNER_CLEAR_VAR}, 0px))`;

/** Below `md`: a FIXED BOTTOM BANNER's offset — 8px above the 64px nav, plus
 *  any safe-area inset (the SHEET_BOTTOM figure as a class). First caller:
 *  `CookieConsentBanner.js`, which sat ON the nav (mobile batch item 25).
 *  Pages with no nav (login) get the same float; it is still clear of the edge. */
export const NAV_CLEAR_BOTTOM = 'max-md:bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))]';
